import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ArrowRight, TrendingUp } from 'lucide-react';
import SectionHeading from './SectionHeading';
import { Slider } from './ui/slider';
import { calculatorDefaults } from '../mock/mock';

const fmt = (n) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });
const money = (n) => '$' + fmt(n);

function Control({ label, value, display, min, max, step, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-slate-600">{label}</label>
        <span className="font-display text-sm font-semibold text-slate-900 tabular-nums">{display}</span>
      </div>
      <Slider data-testid={testid} value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} className="cursor-pointer" />
    </div>
  );
}

export default function CalculatorSection() {
  const [budget, setBudget] = useState(calculatorDefaults.budget);
  const [cpi, setCpi] = useState(calculatorDefaults.cpi);
  const [cvr, setCvr] = useState(calculatorDefaults.cvr);
  const [arpu, setArpu] = useState(calculatorDefaults.arpu);

  const r = useMemo(() => {
    const installs = cpi > 0 ? budget / cpi : 0;
    const payingUsers = installs * (cvr / 100);
    const revenue = payingUsers * arpu;
    const roas = budget > 0 ? revenue / budget : 0;
    const cpa = payingUsers > 0 ? budget / payingUsers : 0;
    return { installs, payingUsers, revenue, roas, cpa };
  }, [budget, cpi, cvr, arpu]);

  const results = [
    { label: 'Projected Installs', value: fmt(r.installs) },
    { label: 'Paying Users', value: fmt(r.payingUsers) },
    { label: 'Projected Revenue', value: money(r.revenue) },
    { label: 'Cost per Paying User', value: r.cpa ? money(r.cpa) : '—' },
  ];

  return (
    <section id="calculator" className="relative max-w-7xl mx-auto px-5 md:px-8 section-pad">
      <SectionHeading
        eyebrow="Growth Calculator"
        title={<span>Model your <span className="text-gradient">acquisition ROI.</span></span>}
        subtitle="Adjust your budget and targets to instantly project installs, paying users, revenue and ROAS."
      />

      <div className="reveal glass-strong rounded-3xl p-6 md:p-9 grid lg:grid-cols-5 gap-8" data-testid="roas-calculator">
        {/* Inputs */}
        <div className="lg:col-span-3 space-y-7">
          <div className="flex items-center gap-2 text-blue-600">
            <Calculator size={18} />
            <span className="text-sm font-semibold text-slate-900">Your campaign inputs</span>
          </div>
          <Control testid="roas-calculator-budget-slider" label="Monthly Ad Budget" value={budget} display={money(budget)} min={5000} max={1000000} step={5000} onChange={setBudget} />
          <Control testid="roas-calculator-cpi-slider" label="Target CPI (cost per install)" value={cpi} display={'$' + cpi.toFixed(2)} min={0.2} max={10} step={0.1} onChange={setCpi} />
          <Control testid="roas-calculator-cvr-slider" label="Install → Paying User Rate" value={cvr} display={cvr.toFixed(1) + '%'} min={0.5} max={40} step={0.5} onChange={setCvr} />
          <Control testid="roas-calculator-arpu-slider" label="Revenue per Paying User (ARPU)" value={arpu} display={money(arpu)} min={2} max={500} step={1} onChange={setArpu} />
          <p className="text-xs text-slate-400 pt-1">Projections are illustrative estimates based on your inputs — not guaranteed results.</p>
        </div>

        {/* Results */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl bg-gradient-to-br from-[#0f172a] to-[#0b132b] p-6 h-full flex flex-col">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Projected ROAS</span>
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400"><TrendingUp size={12} /> live</span>
            </div>
            <div className="font-display text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 mt-1" data-testid="roas-calculator-result">{r.roas.toFixed(2)}x</div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {results.map((m) => (
                <div key={m.label} className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <div className="text-[11px] text-slate-400">{m.label}</div>
                  <div className="font-display text-lg font-bold text-white mt-0.5 tabular-nums">{m.value}</div>
                </div>
              ))}
            </div>
            <Link to="/contact" className="group mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 font-semibold text-white hover:bg-blue-500 transition-colors shadow-lg shadow-blue-600/30">
              Get a tailored media plan <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
